import { apiClient } from '@/lib/api-client';
import type {
  SignInResponse,
  ForgotPasswordInput,
  ResetPasswordInput,
  AuthMethod,
  MeResponse,
} from '../../domain/entities/auth.entity';

export class AuthApiDatasource {
  async signInWithFirebaseToken(
    firebaseToken: string,
  ): Promise<SignInResponse> {
    const { data } = await apiClient.post<SignInResponse>('/auth/sign-in', {
      firebaseUser: firebaseToken,
    });
    return data;
  }

  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    await apiClient.post('/auth/forgot-password', input);
  }

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    await apiClient.post('/auth/reset-password', {
      token: input.token,
      newPassword: input.password,
    });
  }

  // Só `newPassword` atravessa o HTTP: o back roda com forbidNonWhitelisted e
  // rejeita campos extras (400). A `currentPassword` é consumida no repositório
  // (reautenticação no Firebase) e nunca sai do navegador.
  async changePassword(input: { newPassword: string }): Promise<void> {
    await apiClient.post('/auth/change-password', {
      newPassword: input.newPassword,
    });
  }

  // Confirma o e-mail a partir do token do link (nosso ActionToken, não oobCode).
  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/auth/verify-email', { token });
  }

  // Reenvia o e-mail de verificação. A rota é sem path param — o e-mail vai no
  // corpo (a pessoa pode nem estar logada, ex.: muro de verificação).
  async sendVerification(email: string): Promise<void> {
    await apiClient.post('/auth/send-verification', { email });
  }

  /**
   * Pede ao **gateway** um custom token para o usuário logado (SSO handoff →
   * outra plataforma). O gateway minta para o uid de quem chamou e só para ele:
   * é "eu mesmo, em outro lugar" — não é troca de identidade.
   *
   * O interceptor do `apiClient` anexa o Firebase ID token no Bearer.
   */
  async createHandoffToken(): Promise<{ customToken: string }> {
    const { data } = await apiClient.post<{ customToken: string }>(
      '/auth/handoff',
      {},
    );
    return data;
  }
  // Relê a conta do usuário logado (e-mail atual, troca pendente e as formas de
  // login vinculadas).
  async getMe(): Promise<MeResponse> {
    const { data } = await apiClient.get<MeResponse>('/auth/me');
    return data;
  }

  // ── Formas de login (google-sign-in §4.2) ───────────────────────────────────
  // Reflete no backend um provedor que o usuário acabou de vincular no Firebase
  // (client-side). Idempotente: devolve a conta já com `authMethods` atualizado.
  async syncIdentities(): Promise<MeResponse> {
    const { data } = await apiClient.post<MeResponse>(
      '/auth/identities/sync',
      {},
    );
    return data;
  }

  // Desvincula uma forma de login do próprio usuário. Exige autenticação
  // recente no backend (RecentAuthGuard) — quem chama reautentica antes.
  async unlinkIdentity(method: AuthMethod): Promise<MeResponse> {
    const { data } = await apiClient.delete<MeResponse>(
      `/auth/identities/${encodeURIComponent(method)}`,
    );
    return data;
  }

  // ── Troca de e-mail (change-email §4.3) ──────────────────────────────────────
  // Ato 1: sem corpo (o e-mail novo só é pedido no ato 2). Só `newPassword` da
  // reautenticação morre no repositório — nada extra atravessa o HTTP.
  async startEmailChange(): Promise<void> {
    await apiClient.post('/auth/change-email/start', {});
  }

  // Ato 2 (rota pública): o link do e-mail atual traz o token START.
  async submitEmailChange(input: {
    token: string;
    newEmail: string;
  }): Promise<void> {
    await apiClient.post('/auth/change-email/submit', {
      token: input.token,
      newEmail: input.newEmail,
    });
  }

  // Ato 3 (rota pública): o link do e-mail novo traz o token CONFIRM.
  async confirmEmailChange(token: string): Promise<void> {
    await apiClient.post('/auth/change-email/confirm', { token });
  }

  // Cancela uma troca pendente (só autenticado).
  async cancelEmailChange(): Promise<void> {
    await apiClient.delete('/auth/change-email');
  }
}
